/**
 * Browser-side usage-analytics emitter. Buffers events in memory and ships
 * them to the `/api/usage-events` route, which forwards authenticated batches
 * to the agent API's `POST /api/telemetry/events` ingest.
 *
 * Consent-gated: events only leave the browser once the user's usage-tracking
 * consent is confirmed accepted (`consentGranted === true`). While consent is
 * still unknown (query in flight) a small buffer accumulates; a definitive
 * "not accepted" drops everything, locally, before any network I/O — the
 * server enforces the same gate, this is belt-and-suspenders.
 *
 * Delivery is best-effort, mirroring lib/logging/client-logger.ts:
 *  - periodic flush every FLUSH_INTERVAL_MS,
 *  - immediate flush once the buffer reaches MAX_USAGE_EVENT_BATCH,
 *  - a final flush on `pagehide` / tab-hide via `navigator.sendBeacon`, which
 *    survives navigation where a normal `fetch` would be cancelled.
 *
 * SSR-safe: every browser API is touched inside functions guarded on
 * `typeof window`, so importing this module on the server is a no-op.
 */
import {
  MAX_USAGE_EVENT_BATCH,
  MAX_USAGE_EVENT_PROPS_CHARS,
  USAGE_EVENT_TYPE_PATTERN,
  USAGE_EVENTS_ENDPOINT,
  type UsageEvent,
} from "./usage-events-config";

const FLUSH_INTERVAL_MS = 5000;

/** Buffer cap while consent is still unresolved — newest events are dropped
 *  beyond this so an unanswered consent query can't grow memory unbounded. */
const MAX_PENDING_WHILE_CONSENT_UNKNOWN = 20;

const SESSION_ID_STORAGE_KEY = "scirix.usage_session_id";

let buffer: UsageEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let installed = false;

/** null = consent not yet resolved; false = declined/absent (hard no-op). */
let consentGranted: boolean | null = null;
let activeNamespaceId: string | null = null;
let cachedSessionId: string | null = null;

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function nowIso(): string {
  return new Date().toISOString();
}

/** Opaque per-tab session id, persisted in sessionStorage across reloads. */
function getSessionId(): string | null {
  if (!isBrowser()) return null;
  if (cachedSessionId) return cachedSessionId;
  try {
    const existing = window.sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
    if (existing) {
      cachedSessionId = existing;
      return existing;
    }
    const created = crypto.randomUUID();
    window.sessionStorage.setItem(SESSION_ID_STORAGE_KEY, created);
    cachedSessionId = created;
    return created;
  } catch {
    // sessionStorage unavailable (privacy mode) — events go out unsessioned.
    return null;
  }
}

/** Clamp props by serialized size; drop them entirely if oversized/unserializable. */
function clampProps(props?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!props) return undefined;
  try {
    return JSON.stringify(props).length > MAX_USAGE_EVENT_PROPS_CHARS ? undefined : props;
  } catch {
    return undefined;
  }
}

/**
 * Wire consent + attribution into the emitter. Call from React whenever the
 * onboarding-state query or the namespace selection changes; idempotent.
 *
 * Flipping consent to `false` drops the pending buffer on the spot; flipping
 * to `true` releases anything buffered while the query was in flight.
 */
export function setAnalyticsContext(context: {
  consentGranted?: boolean | null;
  namespaceId?: string | null;
}): void {
  if ("namespaceId" in context) {
    activeNamespaceId = context.namespaceId ?? null;
  }
  if ("consentGranted" in context) {
    const next = context.consentGranted ?? null;
    if (next !== consentGranted) {
      consentGranted = next;
      if (next === false) buffer = [];
      if (next === true) flush(false);
    }
  }
}

/**
 * Record a usage event (names in docs/telemetry-events.md). Props must be
 * numbers, enums, or short ids only — never free text or user content.
 * No-ops on the server, when consent is declined, or on a malformed name.
 */
export function track(eventType: string, props?: Record<string, unknown>): void {
  if (!isBrowser() || consentGranted === false) return;
  if (!USAGE_EVENT_TYPE_PATTERN.test(eventType)) return;
  if (consentGranted === null && buffer.length >= MAX_PENDING_WHILE_CONSENT_UNKNOWN) return;

  buffer.push({
    eventType,
    surface: "ui",
    namespaceId: activeNamespaceId,
    sessionId: getSessionId(),
    // No build/version identifier is exposed to the bundle today; the field
    // stays null until one exists (server treats it as optional).
    clientVersion: null,
    props: clampProps(props),
    occurredAt: nowIso(),
  });

  if (consentGranted === true && buffer.length >= MAX_USAGE_EVENT_BATCH) flush();
}

/**
 * Send the buffered batch — only once consent is affirmatively granted. With
 * `useBeacon` (page unloading) we prefer `sendBeacon`; otherwise a `keepalive`
 * fetch that can also outlive the page.
 */
function flush(useBeacon = false): void {
  if (!isBrowser() || consentGranted !== true || buffer.length === 0) return;
  const batch = buffer;
  buffer = [];
  const body = JSON.stringify({ events: batch });

  try {
    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(USAGE_EVENTS_ENDPOINT, new Blob([body], { type: "application/json" }));
      return;
    }
    void fetch(USAGE_EVENTS_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      // Best-effort: drop on network failure rather than retry/grow unbounded.
    });
  } catch {
    // Never let analytics throw into application code.
  }
}

/**
 * Install periodic flushing + final-flush listeners. Idempotent; returns a
 * cleanup function. Call once from a client component mounted in the layout.
 */
export function installUsageTracker(): () => void {
  if (!isBrowser() || installed) return () => {};
  installed = true;

  flushTimer = setInterval(() => flush(false), FLUSH_INTERVAL_MS);
  flushTimer.unref?.();

  const onPageHide = () => flush(true);
  const onVisibility = () => {
    if (document.visibilityState === "hidden") flush(true);
  };

  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    window.removeEventListener("pagehide", onPageHide);
    document.removeEventListener("visibilitychange", onVisibility);
    if (flushTimer) clearInterval(flushTimer);
    flushTimer = null;
    installed = false;
    flush(false);
  };
}
